import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Package, Pencil } from "lucide-react";

import { sportsService, type Equipment, type Sport } from "../../services/sportsService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { EQUIPMENT_MOVES, EQUIPMENT_TYPES, afterMove, day, equipmentConfig, equipmentStatus, labelFrom } from "./sportsLogic";

function EquipmentForm({ item, sports, onClose, onDone }: { item?: Equipment; sports: Sport[]; onClose: () => void; onDone: (row: Equipment) => void }) {
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (item ? sportsService.updateEquipment({ id: item.id, data }) : sportsService.createEquipment(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim() || null;
    save.mutate({
      name: text("name"),
      equipment_type: text("equipment_type"),
      sport: text("sport") ? Number(text("sport")) : null,
      storage_location: text("location"),
      description: text("description"),
      ...(item ? { is_active: form.get("active") === "on" } : { is_active: true }),
    });
  };
  return (
    <FormModal
      id="equipment"
      title={item ? `Edit ${item.name}` : "Add equipment"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The equipment could not be saved.") : undefined}
      saveLabel={item ? "Save changes" : "Add it"}
      note={item ? "Counts change only through movements." : "It starts at zero; record the stock you hold as “New stock in”."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The equipment" divided={false}>
        <Field label="Name" hint="e.g. Size 5 footballs, Netball bibs (set of 7).">
          <input required name="name" maxLength={200} defaultValue={item?.name} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select name="equipment_type" defaultValue={item?.equipment_type ?? "ball"} className="input mt-1">
            {EQUIPMENT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="For" optional>
          <select name="sport" defaultValue={item?.sport ?? ""} className="input mt-1">
            <option value="">Any sport</option>
            {sports.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Kept at" optional>
          <input name="location" maxLength={200} defaultValue={item?.storage_location ?? ""} className="input mt-1" placeholder="e.g. Sports store" />
        </Field>
        {item && (
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" name="active" defaultChecked={item.is_active} className="h-4 w-4 rounded border-gray-300" /> Still in use
          </label>
        )}
        <Field label="Description" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={item?.description ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Balls, kit and gear: how many there are, how many are out or damaged, and every movement. */
export function EquipmentPanel({ equipment, sports, isLoading, isError, onRetry, onChanged }: {
  equipment?: Equipment[];
  sports: Sport[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Equipment | "new" | null>(null);
  const [move, setMove] = useState("receipt");
  const [quantity, setQuantity] = useState("");
  const ledger = useQuery({ queryKey: ["sports", "equipment-ledger", viewing], queryFn: () => sportsService.getEquipmentLedger(viewing!), enabled: viewing !== null });
  const transact = useMutation({
    mutationFn: sportsService.transactEquipment,
    onSuccess: ({ equipment: row, transaction }) => {
      setQuantity("");
      setNotice({ tone: "success", text: `${row.name}: ${transaction.quantity} ${EQUIPMENT_MOVES.find((entry) => entry.value === transaction.transaction_type)?.label.toLowerCase() ?? transaction.transaction_type}. ${row.available_quantity} of ${row.total_quantity} available.` });
      ledger.refetch();
      onChanged();
    },
  });

  const columns: RegisterColumn<Equipment>[] = [
    { header: "Equipment", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Kind", cell: (row) => labelFrom(EQUIPMENT_TYPES, row.equipment_type), exportValue: (row) => labelFrom(EQUIPMENT_TYPES, row.equipment_type), className: "text-gray-700" },
    { header: "For", cell: (row) => row.sport_name || "Any sport", exportValue: (row) => row.sport_name ?? "", className: "text-gray-700" },
    { header: "Available", cell: (row) => row.available_quantity, exportValue: (row) => row.available_quantity, align: "right" },
    { header: "Out", cell: (row) => row.total_quantity - row.available_quantity - row.damaged_quantity, exportValue: (row) => row.total_quantity - row.available_quantity - row.damaged_quantity, align: "right" },
    { header: "Damaged", cell: (row) => <span className={row.damaged_quantity ? "text-amber-800" : undefined}>{row.damaged_quantity}</span>, exportValue: (row) => row.damaged_quantity, align: "right" },
    { header: "Total", cell: (row) => row.total_quantity, exportValue: (row) => row.total_quantity, align: "right" },
    { header: "Kept at", cell: (row) => row.storage_location || "—", exportValue: (row) => row.storage_location ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...equipmentStatus(row)} />, exportValue: (row) => equipmentStatus(row).label },
  ];

  const item = viewing !== null ? equipment?.find((row) => row.id === viewing) : undefined;
  const chosen = EQUIPMENT_MOVES.find((entry) => entry.value === move)!;
  const next = item && Number(quantity) > 0 ? afterMove(item, move, Math.round(Number(quantity))) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Equipment"
        description="Open an item to record stock in, issues, returns, damage and repairs."
        actions={
          <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Package className="mr-1.5 h-3.5 w-3.5" /> Add equipment
          </button>
        }
        rows={equipment}
        config={equipmentConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          transact.reset();
          setMove(row.total_quantity ? "issue" : "receipt");
          setQuantity("");
          setViewing(row.id);
        }}
        openLabel={() => "Move stock"}
        noun={["item", "items"]}
        exportName={{ file: "sports-equipment", title: "Sports Equipment" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No equipment recorded."
      />

      {item && !editing && (
        <DetailsModal
          size="3xl"
          title={item.name}
          headline={{ label: "Available", value: `${item.available_quantity} of ${item.total_quantity}`, note: `${item.total_quantity - item.available_quantity - item.damaged_quantity} out · ${item.damaged_quantity} damaged` }}
          status={equipmentStatus(item)}
          facts={[
            ["Kind", labelFrom(EQUIPMENT_TYPES, item.equipment_type)],
            ["For", item.sport_name || "Any sport"],
            ["Kept at", item.storage_location ?? undefined],
          ]}
          sections={[{ title: "Description", body: item.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(item)}>
                <Pencil className="h-4 w-4" /> Edit
              </button>
            </>
          }
        >
          <ReasonPanel
            key={`${item.total_quantity}-${item.available_quantity}-${item.damaged_quantity}`}
            title="Record a movement"
            label="Why"
            placeholder={move === "issue" ? "e.g. For the under-14 match at St Mary's." : "e.g. Delivered by the supplier."}
            context={{ key: "sports.note", params: { kind: "equipment", id: item.id, transaction_type: move, quantity: quantity || undefined } }}
            button="Record it"
            busy={transact.isPending}
            error={transact.error}
            errorText="The movement could not be recorded."
            onSubmit={(reason) => next && transact.mutate({ id: item.id, data: { transaction_type: move, quantity: Math.round(Number(quantity)), reason } })}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Movement" hint={chosen.hint}>
                <select value={move} onChange={(event) => setMove(event.target.value)} className="input mt-1">
                  {EQUIPMENT_MOVES.map((entry) => <option key={entry.value} value={entry.value}>{entry.label}</option>)}
                </select>
              </Field>
              <Field
                label="How many"
                hint={Number(quantity) > 0 ? (next ? `After: ${next.available} available, ${next.damaged} damaged, ${next.total} in all.` : <span className="text-red-700">More than there is in that state.</span>) : undefined}
              >
                <input type="number" min={1} step={1} value={quantity} onChange={(event) => setQuantity(event.target.value)} className="input mt-1 tabular-nums" />
              </Field>
            </div>
          </ReasonPanel>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Movements</h4>
            {ledger.data?.length ? (
              <div className="sheet-wrap mt-1.5 max-h-72 overflow-y-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Movement</th>
                      <th className="text-right">How many</th>
                      <th>Why</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...ledger.data].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap tabular-nums">{day(row.created_at)}</td>
                        <td>{EQUIPMENT_MOVES.find((entry) => entry.value === row.transaction_type)?.label ?? row.transaction_type}</td>
                        <td className="text-right tabular-nums">{row.quantity}</td>
                        <td className="min-w-[12rem] whitespace-normal text-gray-700">{row.reason}</td>
                        <td className="text-gray-700">{row.recorded_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">{ledger.isLoading ? "Loading…" : "No movements yet."}</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <EquipmentForm
          item={editing === "new" ? undefined : editing}
          sports={sports}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} added. Record the stock you hold as “New stock in”.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
